import React, { useEffect, useMemo, useState } from "react";
import {
  Empty, Input, Select, Spin, Tooltip as AntdTooltip,
} from "antd";
import { CloseOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import {
  Brush,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import dashboardChartTheme from "../../Dashboard/dashboardChartTheme";
import DashboardBrushTraveller from "../../Dashboard/DashboardBrushTraveller";
import {
  buildRcaTimelineChartSeries,
  RCA_AGGREGATE_OPTIONS,
} from "../utils/rcaUtils";
import "../../Dashboard/DashboardShared.css";
import "../../Dashboard/TestMetrics.css";

/** Stroke + soft fill for the fixed Created / Completed lines. */
const TIMELINE_SERIES_PALETTE = [
  {
    color: dashboardChartTheme.sampleSeries.product,
    backgroundColor: "rgba(83, 85, 147, 0.12)",
  },
  {
    color: dashboardChartTheme.testMetricsSeries.onTime,
    backgroundColor: "rgba(90, 170, 110, 0.12)",
  },
];

const TIMELINE_DOT_RADIUS = 4;
const TIMELINE_ACTIVE_DOT_RADIUS = 5;
const TIMELINE_HOVER_STROKE = dashboardChartTheme.sampleSeries.product;
const TIMELINE_CHART_HEIGHT = 360;
const TIMELINE_BRUSH_GUTTER = 20;

/** Compact brush edge labels (full value remains on the chart axis / tooltip). */
const formatBrushTickLabel = (value) => {
  const label = String(value ?? "");
  if (label.length <= 10) {
    return label;
  }
  return `${label.slice(0, 9)}…`;
};

const CHART_TOOLTIP_WRAPPER_STYLE = {
  pointerEvents: "auto",
  zIndex: 20,
};

const BAR_SEGMENT_COLORS = [
  dashboardChartTheme.sampleSeries.product,
  dashboardChartTheme.testMetricsSeries.pending,
  dashboardChartTheme.testMetricsSeries.onTime,
  dashboardChartTheme.testMetricsSeries.delayed,
  dashboardChartTheme.sampleSeries.environment,
  dashboardChartTheme.testMetricsSeries.total,
  "#8B6F47",
  "#4A7C8F",
  "#9B6B9E",
  "#5B8A9A",
];

const getBarSegmentColor = (index) => (
  BAR_SEGMENT_COLORS[index % BAR_SEGMENT_COLORS.length]
);

const getTimelineSeriesPalette = (seriesIndex) => (
  TIMELINE_SERIES_PALETTE[Math.max(seriesIndex, 0) % TIMELINE_SERIES_PALETTE.length]
);

/** Keep wheel / drag on the tooltip from bubbling into the Recharts canvas. */
const stopTooltipScrollPropagation = (event) => {
  event.stopPropagation();
};

/**
 * Shared RCA chart tooltip shell — matches Overdue / Retest layout.
 */
function RcaAnalyticsTooltip({
  active,
  title,
  rows,
  onViewDetails,
  viewDetailsHint,
  viewDetailsLabel = "Click to view details >",
}) {
  if (!active || !title) {
    return null;
  }

  const tooltipRows = Array.isArray(rows) ? rows : [];

  const handleViewDetails = (event) => {
    event.stopPropagation();
    if (onViewDetails) {
      onViewDetails();
    }
  };

  return (
    <div
      className="DataOpsAnalytics__ChartTooltip"
      onMouseDown={stopTooltipScrollPropagation}
      onTouchStart={stopTooltipScrollPropagation}
    >
      <div
        className={
          viewDetailsHint
            ? "DataOpsAnalytics__ChartTooltipHeader DataOpsAnalytics__ChartTooltipHeader--barHint"
            : "DataOpsAnalytics__ChartTooltipHeader"
        }
      >
        <span className="DataOpsAnalytics__ChartTooltipTitle">{title}</span>
        {viewDetailsHint ? (
          <span
            className="DataOpsAnalytics__ChartTooltipBarDetails DataOpsAnalytics__ChartTooltipBarDetails--hint"
          >
            {viewDetailsHint}
          </span>
        ) : null}
        {!viewDetailsHint && onViewDetails ? (
          <button
            type="button"
            className="DataOpsAnalytics__ChartTooltipViewDetails DataOpsAnalytics__ChartTooltipBarDetails"
            onClick={handleViewDetails}
          >
            {viewDetailsLabel}
          </button>
        ) : null}
      </div>
      <div
        className="DataOpsAnalytics__ChartTooltipList"
        onWheel={stopTooltipScrollPropagation}
        onScroll={stopTooltipScrollPropagation}
      >
        {tooltipRows.map((row) => (
          <div key={row.key} className="DataOpsAnalytics__ChartTooltipRow">
            <div className="DataOpsAnalytics__ChartTooltipRowLabel">
              <span
                className="DataOpsAnalytics__ChartTooltipDot"
                style={{ backgroundColor: row.color }}
              />
              <span>{row.label}</span>
            </div>
            <span className="DataOpsAnalytics__ChartTooltipValue">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TimelineTrendCursor({ points, height }) {
  if (!points?.length) {
    return null;
  }
  const { x } = points[0];
  return (
    <line
      x1={x}
      y1={0}
      x2={x}
      y2={height}
      stroke={TIMELINE_HOVER_STROKE}
      strokeWidth={1}
      strokeDasharray="4 4"
    />
  );
}

function TimelineTrendTooltip({
  active,
  payload,
  seriesColorByKey,
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload;
  if (!point) {
    return null;
  }

  const tooltipRows = [...payload]
    .filter((entry) => entry.value != null)
    .map((entry) => ({
      key: entry.dataKey,
      label: entry.name,
      value: String(entry.value ?? 0),
      color: seriesColorByKey?.[entry.dataKey]?.color || entry.color,
    }));

  return (
    <RcaAnalyticsTooltip
      active={active}
      title={point.periodLabel}
      rows={tooltipRows}
    />
  );
}

function ChartEmptyState({ title, description }) {
  return (
    <div className="DataOpsAnalytics__ChartEmpty">
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={description || `No data for ${title}`}
      />
    </div>
  );
}

/**
 * Progress ranking list — same chart_breakdown points as bars, easier scan at scale.
 * Click a row to open the same drill-down modal as the bar charts.
 */
function resolveProgressBarWidth(percent, count, totalCount) {
  if (Number.isFinite(percent) && percent >= 0) {
    return Math.min(percent, 100);
  }
  if (totalCount > 0) {
    return Math.round((count / totalCount) * 100);
  }
  return 0;
}

/** Same Count / Percent rows as the bar-chart hover tooltip. */
function buildProgressStatsTooltipRows(count, displayPercent, colorIndex) {
  return [
    {
      key: "count",
      label: "Count",
      value: String(count ?? 0),
      color: getBarSegmentColor(colorIndex),
    },
    {
      key: "percent",
      label: "Percent",
      value: `${displayPercent ?? 0}%`,
      color: getBarSegmentColor(1),
    },
  ];
}

function RcaProgressStatsList({
  series,
  emptyTitle,
  nameColumnLabel = "Name",
  searchPlaceholder = "Search…",
  onRowClick,
}) {
  const [searchText, setSearchText] = useState("");

  const rankedSeries = useMemo(() => {
    const safeSeries = Array.isArray(series) ? series : [];
    return [...safeSeries].sort((rowA, rowB) => (
      (Number(rowB.count) || 0) - (Number(rowA.count) || 0)
    ));
  }, [series]);

  const filteredSeries = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return rankedSeries;
    }
    return rankedSeries.filter((row) => (
      String(row.name ?? "").toLowerCase().includes(query)
    ));
  }, [rankedSeries, searchText]);

  const totalCount = useMemo(
    () => rankedSeries.reduce((sum, row) => sum + (Number(row.count) || 0), 0),
    [rankedSeries],
  );

  if (!rankedSeries.length) {
    return <ChartEmptyState title={emptyTitle} />;
  }

  return (
    <div className="DataOpsAnalytics__ProgressStats">
      {rankedSeries.length > 8 ? (
        <div className="DataOpsAnalytics__ProgressStatsSearch">
          <Input
            allowClear
            size="small"
            prefix={<SearchOutlined aria-hidden />}
            placeholder={searchPlaceholder}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            className="DataOpsAnalytics__ProgressStatsSearchInput"
          />
        </div>
      ) : null}

      <div
        className="DataOpsAnalytics__ProgressStatsHeader"
        aria-hidden
      >
        <span className="DataOpsAnalytics__ProgressStatsHeaderName">
          {nameColumnLabel}
        </span>
        <span className="DataOpsAnalytics__ProgressStatsHeaderCount">Count</span>
        <span className="DataOpsAnalytics__ProgressStatsHeaderPercent">Percentage</span>
      </div>

      <div className="DataOpsAnalytics__ProgressStatsList">
        {filteredSeries.length === 0 ? (
          <div className="DataOpsAnalytics__ProgressStatsEmptyFilter">
            No matches for “
            {searchText.trim()}
            ”
          </div>
        ) : (
          filteredSeries.map((row, index) => {
            const count = Number(row.count) || 0;
            const percent = Number(row.percent);
            const barWidth = resolveProgressBarWidth(percent, count, totalCount);
            const displayPercent = Number.isFinite(percent) ? percent : barWidth;
            const rowName = row.name || "Unknown";

            return (
              <AntdTooltip
                key={row.key || `${row.name}-${index}`}
                placement="topLeft"
                mouseEnterDelay={0.2}
                destroyTooltipOnHide
                overlayClassName="DataOpsAnalytics__ProgressStatsTooltipOverlay"
                title={(
                  <RcaAnalyticsTooltip
                    active
                    title={rowName}
                    rows={buildProgressStatsTooltipRows(count, displayPercent, index)}
                    viewDetailsHint="Click to view details >"
                  />
                )}
              >
                <button
                  type="button"
                  className="DataOpsAnalytics__ProgressStatsRow"
                  onClick={() => {
                    if (onRowClick) {
                      onRowClick(row);
                    }
                  }}
                >
                  <span className="DataOpsAnalytics__ProgressStatsName">
                    {rowName}
                  </span>
                  <span className="DataOpsAnalytics__ProgressStatsCount">{count}</span>
                  <span className="DataOpsAnalytics__ProgressStatsPercentCell">
                    <span className="DataOpsAnalytics__ProgressStatsBarTrack" aria-hidden>
                      <span
                        className="DataOpsAnalytics__ProgressStatsBarFill"
                        style={{
                          width: `${barWidth}%`,
                          backgroundColor: getBarSegmentColor(index),
                        }}
                      />
                    </span>
                    <span className="DataOpsAnalytics__ProgressStatsPercent">
                      {`${displayPercent}%`}
                    </span>
                  </span>
                </button>
              </AntdTooltip>
            );
          })
        )}
      </div>

      <div className="DataOpsAnalytics__ProgressStatsFooter">
        <span className="DataOpsAnalytics__ProgressStatsTotalLabel">Total</span>
        <span className="DataOpsAnalytics__ProgressStatsTotalValue">{totalCount}</span>
        <span className="DataOpsAnalytics__ProgressStatsFooterMeta">
          {filteredSeries.length === rankedSeries.length
            ? `${rankedSeries.length} items`
            : `${filteredSeries.length} of ${rankedSeries.length} items`}
        </span>
      </div>
    </div>
  );
}

function TimelineSeriesPill({
  label,
  color,
  backgroundColor,
  isSelected,
  onToggle,
}) {
  return (
    <button
      type="button"
      className={
        isSelected
          ? "DataOpsAnalytics__SlaPill DataOpsAnalytics__SlaPill--active"
          : "DataOpsAnalytics__SlaPill DataOpsAnalytics__SlaPill--inactive"
      }
      style={isSelected ? { borderColor: color, color, backgroundColor } : undefined}
      onClick={onToggle}
      aria-pressed={isSelected}
    >
      <span className="DataOpsAnalytics__SlaPillLabel">{label}</span>
      {isSelected
        ? <CloseOutlined className="DataOpsAnalytics__SlaPillIcon" aria-hidden />
        : <PlusOutlined className="DataOpsAnalytics__SlaPillIcon" aria-hidden />}
    </button>
  );
}

function TimelineTrendDot({
  cx,
  cy,
  stroke,
  value,
  radius = TIMELINE_DOT_RADIUS,
}) {
  if (value == null || cx == null || cy == null) {
    return null;
  }

  return (
    <circle
      cx={cx}
      cy={cy}
      r={radius}
      fill="#ffffff"
      stroke={stroke}
      strokeWidth={2}
      className="DataOpsAnalytics__SlaDot"
    />
  );
}

/**
 * Dual-line created vs completed trend — pills + brush aligned with Overdue SLA UX.
 * Exactly two series; search is unnecessary.
 */
function RcaTimelineChart({
  groups,
  aggregate,
}) {
  const [selectedSeriesKeys, setSelectedSeriesKeys] = useState([]);

  const { chartRows, lineDescriptors } = buildRcaTimelineChartSeries(
    groups,
    aggregate,
  );
  const seriesSignature = lineDescriptors.map((descriptor) => descriptor.dataKey).join("|");

  useEffect(() => {
    setSelectedSeriesKeys(
      lineDescriptors.map((descriptor) => descriptor.dataKey),
    );
  }, [seriesSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!chartRows.length) {
    return <ChartEmptyState title="created vs completed" />;
  }

  const seriesColorByKey = {};
  lineDescriptors.forEach((descriptor) => {
    seriesColorByKey[descriptor.dataKey] = getTimelineSeriesPalette(descriptor.seriesIndex);
  });

  const visibleDescriptors = lineDescriptors.filter((descriptor) => (
    selectedSeriesKeys.includes(descriptor.dataKey)
  ));

  const toggleSeries = (dataKey) => {
    setSelectedSeriesKeys((previousKeys) => (
      previousKeys.includes(dataKey)
        ? previousKeys.filter((key) => key !== dataKey)
        : [...previousKeys, dataKey]
    ));
  };

  return (
    <div className="DataOpsAnalytics__SlaTrend">
      <div className="DataOpsAnalytics__SlaSeriesControls">
        <div className="DataOpsAnalytics__SlaPills">
          {lineDescriptors.map((descriptor) => {
            const palette = seriesColorByKey[descriptor.dataKey];
            return (
              <TimelineSeriesPill
                key={descriptor.dataKey}
                label={descriptor.legendName}
                color={palette.color}
                backgroundColor={palette.backgroundColor}
                isSelected={selectedSeriesKeys.includes(descriptor.dataKey)}
                onToggle={() => toggleSeries(descriptor.dataKey)}
              />
            );
          })}
        </div>
      </div>

      {visibleDescriptors.length === 0 ? (
        <div className="DataOpsAnalytics__SlaPlaceholder" role="alert">
          Please select at least one series to display the graph.
        </div>
      ) : (
        <div className="DataOpsAnalytics__SlaChartWrapper">
          <div className="DataOpsAnalytics__SlaChartArea">
            <ResponsiveContainer width="100%" height={TIMELINE_CHART_HEIGHT}>
              <LineChart
                data={chartRows}
                margin={{
                  top: 10,
                  right: 16 + TIMELINE_BRUSH_GUTTER,
                  left: TIMELINE_BRUSH_GUTTER,
                  bottom: 10,
                }}
              >
                <CartesianGrid
                  strokeDasharray="4 4"
                  stroke={dashboardChartTheme.gridStroke}
                  vertical
                  horizontal
                />
                <XAxis
                  dataKey="periodLabel"
                  tickLine={false}
                  axisLine={false}
                  tick={{
                    fill: "#6b778c",
                    fontSize: 11,
                    fontFamily: "Roboto",
                  }}
                  interval="preserveStartEnd"
                  padding={{ left: 10, right: 10 }}
                  height={36}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={44}
                  allowDecimals={false}
                  tick={{
                    fill: "#6b778c",
                    fontSize: 11,
                    fontFamily: "Roboto",
                  }}
                  domain={[0, "auto"]}
                />
                <Tooltip
                  wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE}
                  content={(tooltipProps) => (
                    <TimelineTrendTooltip
                      active={tooltipProps.active}
                      payload={tooltipProps.payload}
                      seriesColorByKey={seriesColorByKey}
                    />
                  )}
                  cursor={<TimelineTrendCursor />}
                />
                {visibleDescriptors.map((descriptor) => {
                  const stroke = seriesColorByKey[descriptor.dataKey].color;
                  return (
                    <Line
                      key={descriptor.dataKey}
                      type={dashboardChartTheme.line.curveType}
                      dataKey={descriptor.dataKey}
                      name={descriptor.legendName}
                      stroke={stroke}
                      strokeWidth={dashboardChartTheme.line.strokeWidth}
                      strokeLinecap={dashboardChartTheme.line.strokeLinecap}
                      strokeLinejoin={dashboardChartTheme.line.strokeLinejoin}
                      connectNulls
                      isAnimationActive={false}
                      dot={(dotProps) => (
                        <TimelineTrendDot
                          cx={dotProps.cx}
                          cy={dotProps.cy}
                          value={dotProps.value}
                          stroke={stroke}
                        />
                      )}
                      activeDot={(dotProps) => (
                        <TimelineTrendDot
                          cx={dotProps.cx}
                          cy={dotProps.cy}
                          value={dotProps.value}
                          stroke={TIMELINE_HOVER_STROKE}
                          radius={TIMELINE_ACTIVE_DOT_RADIUS}
                        />
                      )}
                    />
                  );
                })}
                <Brush
                  dataKey="periodLabel"
                  stroke={dashboardChartTheme.brush.stroke}
                  fill={dashboardChartTheme.brush.fill}
                  height={dashboardChartTheme.brush.height}
                  travellerWidth={dashboardChartTheme.brush.travellerWidth}
                  traveller={DashboardBrushTraveller}
                  tickFormatter={formatBrushTickLabel}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function ChartCard({
  title, headerExtra, fullWidth = false, children,
}) {
  return (
    <div
      className={
        fullWidth
          ? "TestMetrics__ChartCard DataOpsAnalytics__ChartCard DataOpsAnalytics__ChartCard--fullWidth"
          : "TestMetrics__ChartCard DataOpsAnalytics__ChartCard"
      }
    >
      <div className="TestMetrics__ChartHeader DataOpsAnalytics__ChartHeader">
        <h3>{title}</h3>
        {headerExtra}
      </div>
      {children}
    </div>
  );
}

function ChartsLoading() {
  return (
    <div className="DataOpsAnalytics__ChartEmpty">
      <Spin tip="Loading charts..." />
    </div>
  );
}

/**
 * RCA charts — created vs completed timeline + bar breakdowns + issue-statistics
 * progress lists (same chart_breakdown data as the bars).
 * Dual-line created vs completed from the same rca_dashboard groups as the table.
 * Aggregate (day|week|month) is FE rollup only.
 */
const EMPTY_BAR_SERIES = { customer: [], test: [], assignee: [] };

export default function RcaCharts({
  groups,
  chartBarSeries = EMPTY_BAR_SERIES,
  loading,
  timelineAggregate,
  onTimelineAggregateChange,
  onBarDrillDown,
}) {
  const hasGroups = Array.isArray(groups) && groups.length > 0;
  const chartsLoading = loading && !hasGroups;
  const customerSeries = chartBarSeries.customer || [];
  const testSeries = chartBarSeries.test || [];
  const assigneeSeries = chartBarSeries.assignee || [];

  return (
    <div className="DataOpsAnalytics__ChartsStack">
      <ChartCard
        title="Created vs completed"
        fullWidth
        headerExtra={(
          <div className="TestMetrics__FrequencyDropdown">
            <Select
              value={timelineAggregate}
              onChange={onTimelineAggregateChange}
              options={RCA_AGGREGATE_OPTIONS}
            />
          </div>
        )}
      >
        {chartsLoading
          ? <ChartsLoading />
          : (
            <RcaTimelineChart
              groups={groups}
              aggregate={timelineAggregate}
            />
          )}
      </ChartCard>

      <ChartCard title="Issue Statistics: RCAs by Customer(s)">
        {chartsLoading
          ? <ChartsLoading />
          : (
            <RcaProgressStatsList
              series={customerSeries}
              emptyTitle="customer"
              nameColumnLabel="Customer's Name:"
              searchPlaceholder="Search customers"
              onRowClick={onBarDrillDown}
            />
          )}
      </ChartCard>

      <ChartCard title="Issue Statistics: RCAs by Test(s)">
        {chartsLoading
          ? <ChartsLoading />
          : (
            <RcaProgressStatsList
              series={testSeries}
              emptyTitle="tests"
              nameColumnLabel="Test(s):"
              searchPlaceholder="Search tests"
              onRowClick={onBarDrillDown}
            />
          )}
      </ChartCard>

      <ChartCard title="Issue Statistics: RCAs by Assignee(s)">
        {chartsLoading
          ? <ChartsLoading />
          : (
            <RcaProgressStatsList
              series={assigneeSeries}
              emptyTitle="assignee"
              nameColumnLabel="Assignee:"
              searchPlaceholder="Search assignees"
              onRowClick={onBarDrillDown}
            />
          )}
      </ChartCard>
    </div>
  );
}
